import { useState } from 'react';

// ── Types ────────────────────────────────────────────────────────────────────

interface LateralData {
  riddle: string;
}

interface Props {
  nanoid: string;
  lateral: LateralData;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function LateralPuzzle({ nanoid, lateral }: Props) {
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    feedback: string;
    other_answers: string[];
  } | null>(null);

  async function submitAnswer() {
    if (!answer.trim() || loading || result) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/puzzles/${nanoid}/lateral/evaluate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answer: answer.trim() }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed');
      setResult(data.data);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="pz-lateral">
      <div className="pz-riddle-box">
        <p className="pz-riddle-text">{lateral.riddle}</p>
      </div>

      {result ? (
        <div className="pz-lateral-result">
          <div className="pz-lateral-feedback">
            <p className="pz-lateral-feedback-text">{result.feedback}</p>
          </div>

          {result.other_answers.length > 0 && (
            <div className="pz-lateral-others">
              <p className="pz-lateral-others-label">Other ways to think about it:</p>
              <ul className="pz-lateral-others-list">
                {result.other_answers.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <div className="pz-lateral-input">
          <label className="pz-input-label">Your answer</label>
          <textarea
            className="pz-textarea"
            placeholder="Think outside the box — there's no single right answer..."
            value={answer}
            onChange={e => setAnswer(e.target.value)}
            rows={4}
            disabled={loading}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submitAnswer();
              }
            }}
          />
          {error && <p className="pz-error">{error}</p>}
          <button
            className="pz-btn-primary"
            onClick={submitAnswer}
            disabled={loading || !answer.trim()}
          >
            {loading ? 'Thinking...' : 'Submit Answer'}
          </button>
        </div>
      )}
    </div>
  );
}
